import React, { Component, createRef, type MouseEvent } from 'react';
import isNil from 'lodash/isNil';
import type { CardTransitionZoneInfo } from '@atlassian/jira-platform-board-kit';
import type { TransitionId } from '@atlassian/jira-platform-board-kit/src/common/types';

import { LoadingContainer } from '@atlassian/jira-platform-board-kit/src/ui/column/column-transition-zones-container/loading-container';
import {
	InnerContainer,
	OuterContainer,
} from '@atlassian/jira-platform-board-kit/src/ui/column/column-transition-zones-container/main';
import { NoTransitionContainer } from '@atlassian/jira-platform-board-kit/src/ui/column/column-transition-zones-container/no-transition-container';
import type { ColumnTransitionZonesContainerProps } from '@atlassian/jira-platform-board-kit/src/ui/column/column-transition-zones-container/types';
import ColumnTransitionToZone from './transition-to-zone';
import ColumnTransitionZone from './transition-zone';

type State = {
	allowSelfColumnTransitions: boolean;
	intendToDrop: boolean;
	intendedTransitionId: TransitionId | null;
};

// eslint-disable-next-line jira/react/no-class-components
export class ColumnTransitionZonesContainer extends Component<
	ColumnTransitionZonesContainerProps,
	State
> {
	state = {
		allowSelfColumnTransitions: false,
		intendToDrop: false,
		intendedTransitionId: null,
	};

	containerRef = createRef<HTMLDivElement>();

	onMouseOverTransition = (transitionId: number | null, hoverIntentOnZone: boolean) => {
		const { columnId, columnDraggingOver, onCardDragOver, hoverIntent } = this.props;
		const hoverIntentOnThisColumn = hoverIntent && columnDraggingOver === columnId;
		if (!hoverIntentOnThisColumn) {
			onCardDragOver(transitionId, hoverIntentOnZone);
		}

		// storing the intended transitionId in state, as the ColumnTransitionZone are hidden
		// once hoverIntent is confirmed, and mouseUp event will not trigger from ColumnTransitionZone
		if (!isNil(transitionId) && hoverIntentOnZone) {
			this.setState({ intendedTransitionId: transitionId });
		}
	};

	onMouseLeaveColumn = () => {
		// mouseup event confirms that user intend to drop the card over the transition zone
		// using check `intendToDrop`== true, we wont dispatch onCardDragover
		if (!this.state.intendToDrop) {
			this.props.onCardDragOver(null, false);
		}

		this.setState({
			allowSelfColumnTransitions: false,
			intendToDrop: false,
			intendedTransitionId: null,
		});
	};

	onHoverOverTransitionToZone = () => {
		this.setState({ allowSelfColumnTransitions: true });
	};

	// mouseup event is fired when card is dropped over the transition zone div
	onTransitionSelection = (transitionId: TransitionId) => {
		this.props.onTransitionSelected(transitionId);
		this.setState({ intendToDrop: true, intendedTransitionId: null });
	};

	// When hoverIntent is confirmed & ColumnTransitionZone is hidden
	// and card is dropped in column, no MouseupEvent triggered from `ColumnTransitionZone`,
	// in that case TransitionId is not available.
	// IntendedTransitionId stored in the state is used as selectedTransitionId
	onMouseUpEvent = (e: MouseEvent) => {
		e.stopPropagation();
		if (this.state.intendedTransitionId !== null) {
			this.props.onTransitionSelected(this.state.intendedTransitionId);
		}
		this.setState({ intendToDrop: true, intendedTransitionId: null });
	};

	renderTransitions = (transitions: CardTransitionZoneInfo[]) => {
		const {
			hasSwimlanes,
			hoverIntent,
			sourceStatus,
			notAvailableMessage,
			noTransitionMessage,
			isLoading,
			shouldRenderFullColumnTransitionZone,
			hideSourceStatusInLabel,
			columnId,
			swimlaneId,
		} = this.props;
		if (isLoading) {
			return <LoadingContainer />;
		}

		if (transitions.length === 0 && noTransitionMessage) {
			return (
				<InnerContainer
					data-component-selector="platform-board-kit.ui.column.column-transition-zones-container.inner-container"
					hasSwimlanes={hasSwimlanes}
					zoneCount={transitions.length}
					shouldRenderFullColumnTransitionZone={Boolean(shouldRenderFullColumnTransitionZone)}
					innerRef={this.containerRef}
				>
					<NoTransitionContainer noTransitionMessage={noTransitionMessage} />
				</InnerContainer>
			);
		}
		return (
			<InnerContainer
				data-component-selector="platform-board-kit.ui.column.column-transition-zones-container.inner-container"
				hasSwimlanes={hasSwimlanes}
				zoneCount={transitions.length}
				shouldRenderFullColumnTransitionZone={Boolean(shouldRenderFullColumnTransitionZone)}
				innerRef={this.containerRef}
			>
				{transitions.map(
					({
						transitionId,
						transitionName,
						targetStatusName,
						targetStatusCategory,
						isAvailable,
						targetStatusId,
						isGlobal,
					}) => {
						const filteredSourceStatus = sourceStatus?.id === targetStatusId ? null : sourceStatus;
						const filteredTransitionName =
							sourceStatus?.id === targetStatusId ? null : transitionName;

						return (
							<ColumnTransitionZone
								key={transitionId}
								isHidden={hoverIntent}
								transitionId={transitionId}
								transitionName={filteredTransitionName}
								targetStatusName={targetStatusName}
								onDragOver={this.onMouseOverTransition}
								targetStatusCategory={targetStatusCategory}
								isTransitionAvailable={isAvailable}
								isTransitionGlobal={isGlobal}
								notAvailableMessage={notAvailableMessage}
								sourceStatus={filteredSourceStatus}
								onTransitionSelection={this.onTransitionSelection}
								hideSourceStatusInLabel={hideSourceStatusInLabel}
								columnId={columnId}
								swimlaneId={swimlaneId}
							/>
						);
					},
				)}
			</InnerContainer>
		);
	};

	render() {
		const {
			transitions: allTransitions,
			hasSwimlanes,
			hoverIntent,
			columnId,
			sourceColumnId,
			sourceStatus,
			showAllTransitionZones,
			shouldRenderFullColumnTransitionZone,
		} = this.props;

		let transitions = allTransitions;

		if (!showAllTransitionZones && columnId === sourceColumnId) {
			transitions = allTransitions.filter(
				(t) => !t.isGlobal || (sourceStatus && t.targetStatusId !== sourceStatus.id),
			);

			if (transitions.length === 0) {
				return null;
			}
		}

		if (
			!showAllTransitionZones &&
			columnId === sourceColumnId &&
			!this.state.allowSelfColumnTransitions &&
			!hoverIntent
		) {
			return <ColumnTransitionToZone onHover={this.onHoverOverTransitionToZone} />;
		}

		return (
			<OuterContainer
				data-testid="platform-board-kit-legacy-dnd.ui.column.column-transition-zones-container.outer-container"
				data-component-selector="platform-board-kit.ui.column.column-transition-zones-container.outer-transition-container"
				isHidden={!shouldRenderFullColumnTransitionZone && hoverIntent}
				hasSwimlanes={hasSwimlanes}
				onMouseLeave={this.onMouseLeaveColumn}
				onMouseUp={this.onMouseUpEvent}
				shouldRenderFullColumnTransitionZone={Boolean(shouldRenderFullColumnTransitionZone)}
			>
				{this.renderTransitions(transitions)}
			</OuterContainer>
		);
	}
}
